import { gql } from '@apollo/client'
import { useApolloClient, useMutation } from '@apollo/client/react'
import type * as React from 'react'
import { useRef } from 'react'
import ActionQrDialog from '../components/ActionQrDialog'
import { GetCurrentUserDocument, UserLoginDocument } from '../gql/graphql'
import type { SiteInfo } from '../hooks'

gql`
  mutation UserLogin($code: String, $openid: ID, $userId: ID) {
    login(code: $code, openid: $openid, userId: $userId) {
      ... on LoginResponse {
        authToken
        user {
          openid
          ...BasicUserFields
          sites {
            id
            name
            role
          }
        }
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

interface LoginDialogProps {
  onLoginSuccess: (sites: SiteInfo[]) => void
  onClose?: () => void
}

/**
 * A dialog to be used on a normal browser that prompts the user to log in by scanning
 * a weapp qrcode
 */
const LoginDialog: React.FC<LoginDialogProps> = ({ onLoginSuccess, onClose }) => {
  const apolloClient = useApolloClient()
  const hasLoggedIn = useRef(false)
  const [loginUser] = useMutation(UserLoginDocument)

  const handleScanned = async (openid: string, _userId: string | null | undefined) => {
    if (hasLoggedIn.current) return
    try {
      const { data } = await loginUser({ variables: { openid } })

      const loginResult = data?.login
      if (!loginResult) {
        console.error('Login failed: No data received')
        return
      }

      if (loginResult.__typename === 'GraphqlRequestError') {
        console.error('Login failed:', loginResult.message)
        return
      }

      if (loginResult.__typename === 'LoginResponse') {
        localStorage.setItem('authToken', loginResult.authToken)

        apolloClient.writeQuery({
          query: GetCurrentUserDocument,
          data: {
            __typename: 'Query',
            // biome-ignore lint/suspicious/noExplicitAny: writeQuery type doesn't accept UserBasic
            currentUser: loginResult.user as any,
          },
        })

        hasLoggedIn.current = true
        onLoginSuccess(loginResult.user.sites)
      }
    } catch (apolloError) {
      console.error('Apollo error:', apolloError)
    }
  }

  return (
    <ActionQrDialog
      open
      onClose={onClose}
      action="login"
      title="微信扫码登录"
      onScanned={handleScanned}
    />
  )
}

export default LoginDialog
